import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../auth/auth.service';
import { DashboardService } from './dashboard.service';
import { ExecutiveDashboardComponent } from './executive-dashboard/executive-dashboard.component';
import { FacilitiesDashboardComponent } from './facilities-dashboard/facilities-dashboard.component';
import { FinanceDashboardComponent } from './finance-dashboard/finance-dashboard.component';
import { MapDashboardComponent } from './map-dashboard/map-dashboard.component';
import { MyDashboardComponent } from './my-dashboard/my-dashboard.component';
import { ProcurementDashboardComponent } from './procurement-dashboard/procurement-dashboard.component';
import { RegulatoryDashboardComponent } from './regulatory-dashboard/regulatory-dashboard.component';
import { StaffDashboardComponent } from './staff-dashboard/staff-dashboard.component';
import { SupportDashboardComponent } from './support-dashboard/support-dashboard.component';
import { SysadminDashboardComponent } from './sysadmin-dashboard/sysadmin-dashboard.component';
import { DigitalClockComponent } from './widgets/digital-clock/digital-clock.component';

const DASHBOARD_CATALOG = [
  { type: 'executive', permission: 'dashboard_management', label: 'Executive', icon: 'leaderboard' },
  { type: 'finance', permission: 'accounting_invoices_view', label: 'Finance', icon: 'account_balance' },
  { type: 'facilities', permission: 'inspection_reports_view', label: 'Facilities', icon: 'construction' },
  { type: 'procurement', permission: 'rfp_view', label: 'Procurement', icon: 'shopping_cart' },
  { type: 'regulatory', permission: 'compliance_report_view', label: 'Regulatory', icon: 'gavel' },
  { type: 'support', permission: 'happiness_center_view', label: 'Support', icon: 'support_agent' },
  { type: 'sysadmin', permission: 'users_view', label: 'System Admin', icon: 'admin_panel_settings' },
  { type: 'staff', permission: 'dashboard_staff', label: 'Staff Performance', icon: 'groups' },
  { type: 'map', permission: 'dashboard_mapview', label: 'Map', icon: 'map' },
];

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    NgClass,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    DigitalClockComponent,
    MyDashboardComponent,
    MapDashboardComponent,
    ExecutiveDashboardComponent,
    FinanceDashboardComponent,
    FacilitiesDashboardComponent,
    ProcurementDashboardComponent,
    RegulatoryDashboardComponent,
    SupportDashboardComponent,
    StaffDashboardComponent,
    SysadminDashboardComponent,
  ],
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col w-full flex-auto min-h-0' },
})
export class DashboardComponent implements OnInit {
  protected auth = inject(AuthService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);

  profile: any;
  greeting = '';
  pageLoader = false;
  error: string = null;
  availableDashboards: { type: string; label: string; icon: string }[] = [];

  ngOnInit() {
    this.profile = this.auth.profile();
    this.greeting = this._buildGreeting();

    // Personal dashboard is always available; role dashboards are permission-gated
    const roleDashboards = DASHBOARD_CATALOG.filter((d) => this.auth.canAccess([d.permission]));
    this.availableDashboards = [{ type: 'personal', label: 'Personal', icon: 'person' }, ...roleDashboards];

    this.dashboardSvc.showViewToggle.set(false);

    // Restore the user's last-selected tab if it is still accessible. Map is never restored —
    // even if it was saved before this exclusion existed — it should always fall back to Personal.
    const savedView = this.profile?.view;
    const accessibleTypes = this.availableDashboards.map((d) => d.type);
    const savedIsAccessible = savedView && savedView !== 'map' && accessibleTypes.includes(savedView);

    this.dashboardSvc.dashboardView.set(savedIsAccessible ? savedView : 'personal');
    this.cdr.markForCheck();
  }

  switchDashboard(type: string): void {
    this.dashboardSvc.dashboardView.set(type);

    // Map is never remembered as the "last dashboard" — it should always fall back to Personal
    // on the next login rather than reopening the map view automatically.
    if (type === 'map') {
      this.cdr.markForCheck();
      return;
    }

    // Persist selection so it survives navigation away and back
    this.dashboardSvc.updateProfileView(type).subscribe();
    const profile = this.auth.profile();
    if (profile) {
      this.auth.profile.set({ ...profile, view: type });
    }
    this.cdr.markForCheck();
  }

  private _buildGreeting(): string {
    const hour = new Date().getHours();
    const pools = {
      morning: ['Good morning', 'Rise and shine', 'Ready to make it count', 'A fresh start awaits', 'Start strong today'],
      afternoon: ['Good afternoon', 'Keep the momentum going', "You're doing great", 'Stay focused', 'Halfway through - keep it up'],
      evening: ['Good evening', 'Great work today', 'Time to wrap things up', 'Almost there - finish strong', 'Well done today'],
      night: ['Working late tonight', 'Burning the midnight oil', 'The hustle never stops', 'Dedicated as always', 'The night is yours'],
    };
    const pool = hour >= 5 && hour < 12 ? pools.morning : hour >= 12 && hour < 17 ? pools.afternoon : hour >= 17 && hour < 22 ? pools.evening : pools.night;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  get hasMapTab(): boolean {
    return this.availableDashboards.some((d) => d.type === 'map');
  }

  get currentDashboardLabel(): string {
    const view = this.dashboardSvc.dashboardView();
    if (view === 'personal') return 'Personal';
    return DASHBOARD_CATALOG.find((d) => d.type === view)?.label ?? 'Dashboard';
  }

  get currentDashboardIcon(): string {
    const view = this.dashboardSvc.dashboardView();
    if (view === 'personal') return 'person';
    return DASHBOARD_CATALOG.find((d) => d.type === view)?.icon ?? 'dashboard';
  }
}
